import { useEffect } from "react";
import * as THREE from "three";
import { logger } from "../../utils/logger";

const safeTraverse = (root, visitor) => {
  if (!root || typeof visitor !== "function") return;
  const stack = [root];
  const seen = new Set();
  while (stack.length > 0) {
    const obj = stack.pop();
    if (!obj || seen.has(obj)) continue;
    seen.add(obj);
    try {
      visitor(obj);
    } catch (e) {
      logger.warn("[useSiteVizAppearance] Visitor error during traversal:", e);
    }
    const children = obj.children;
    if (Array.isArray(children) && children.length > 0) {
      for (let i = children.length - 1; i >= 0; i--) stack.push(children[i]);
    }
  }
};

const applySiteMeshColor = (material, color) => {
  if (!material || !material.color) return;
  try {
    material.color.set(color);
    material.needsUpdate = true;
  } catch (e) {
    logger.warn("[useSiteVizAppearance] Failed to set site material color:", e);
  }
};

/**
 * Applies site marker colors: selected site uses {@link siteVizSelectedColor}, others {@link siteVizColor}.
 */
export function useSiteVizAppearance({
  sceneObjects,
  siteVizColor,
  siteVizSelectedColor,
  selectedSite,
}) {
  useEffect(() => {
    const unselected = new THREE.Color();
    const selected = new THREE.Color();
    try {
      unselected.setStyle(siteVizColor);
      selected.setStyle(siteVizSelectedColor);
    } catch (e) {
      logger.warn(
        "[useSiteVizAppearance] Invalid site viz colors:",
        siteVizColor,
        siteVizSelectedColor,
        e
      );
      return;
    }

    Object.values(sceneObjects).forEach((obj) => {
      safeTraverse(obj, (child) => {
        if (!child?.isMesh || child.userData?.type !== "site") return;
        const uuid = child.userData?.siteUuid;
        const isSelected =
          selectedSite != null && uuid != null && String(uuid) === String(selectedSite);
        const c = isSelected ? selected : unselected;
        const mats = child.material;
        if (Array.isArray(mats)) {
          mats.forEach((m) => applySiteMeshColor(m, c));
        } else {
          applySiteMeshColor(mats, c);
        }
      });
    });
  }, [sceneObjects, selectedSite, siteVizColor, siteVizSelectedColor]);
}
